import React from "react";
import ResponsiveImage from "../../atoms/image/ResponsiveImage";
import VideoPlayer from "../common/VideoPlayer";
import {
  viewport,
  colors,
  font,
  lineHeight,
} from "../../../styles/cssinjs/helpers/variables.js";

// Content Components
const Heading = ({ data }) => {
  return (
    <h2
      className="text-lg md:mt-5 md:mb-2"
      dangerouslySetInnerHTML={{ __html: data }}
    />
  );
};
const Para = ({ data }) => {
  return (
    <>
      {Array.isArray(data) ? (
        data.map((item, index) => {
          return (
            <p
              key={index}
              dangerouslySetInnerHTML={{ __html: item }}
              className="lg:mt-2 md:mt-3"
            />
          );
        })
      ) : (
        <p dangerouslySetInnerHTML={{ __html: data }} />
      )}
    </>
  );
};

const Video = ({ data }) => {
  return (
    <VideoPlayer
      data={data}
      id={data.id}
      iframeId="videoUtubeOne"
      videoTagId="vidoTimgOne"
      wrapClass="md:mb-5 lg:mt-3 lg:mb-3"
    />
  );
};

const TableLayout = ({ data }) => {
  return (
    <article className="fx w-100">
      {data.map((item, index) => (
        <div key={index}>
          <span className="d-block text-brandcolor fw-700">{item.head}</span>
          <span className="d-block">{item.data}</span>
        </div>
      ))}
      <style jsx>{`
        article {
          width: auto;
          margin: 12px 0 24px 0;
          padding: 20px 65px 25px 20px;
          border: solid 1px ${colors.primitive.black[300]};
          background-color: ${colors.primitive.black[200]};
          border-radius: 8px;
          font-size: ${font.size.md[1]};
          line-height: ${lineHeight[7]};
        }
        article :global(span) {
          width: 200px;
          height: 32px;
        }
        @media (max-width: ${viewport.tablet.sm}) {
          article {
            flex-direction: column;
            margin: 14px 0 0 0;
            padding: 24px;
            padding-bottom: 11px;
          }
          article :global(span) {
            width: 100%;
            margin-bottom: 22px;
          }
        }
      `}</style>
    </article>
  );
};

const OrderedList = ({ data }) => {
  return (
    <ol className="ordered-bulletin text-sm md:mt-1.5">
      {data.map((item, index) => {
        if (typeof item === "object") {
          return (
            <li
              data-id={index + 1}
              key={index}
              className={
                index === 0 ? "lg:mt-3 md:mb-1.5 pl-0" : "md:mb-1.5 pl-0"
              }
            >
              <span className="ml-5">{item.text}</span>
              <TableLayout data={item.tableLayout} />
            </li>
          );
        }
        return (
          <li
            data-id={index + 1}
            key={index}
            dangerouslySetInnerHTML={{ __html: item }}
            className={index === 0 ? "lg:mt-3 md:mb-1.5" : "md:mb-1.5"}
          />
        );
      })}
    </ol>
  );
};

const UnOrderedList = ({ data }) => {
  return (
    <ul>
      {data.map((item, index) => {
        return <li key={index} dangerouslySetInnerHTML={{ __html: item }} />;
      })}
    </ul>
  );
};
const NoteText = ({ children }) => {
  return (
    <>
      <p className="note" dangerouslySetInnerHTML={{ __html: children }} />
      <style jsx>{`
        .note {
          font-size: ${font.size.sm[3]};
          line-height: ${lineHeight[7]};
          color: ${colors.primitive.grey[800]};
          @media (max-width: ${viewport.tablet.sm}) {
            margin-top: 40px;
          }
        }
      `}</style>
    </>
  );
};
export default function LogoAsideWithContent({ data, className }) {
  const Logo = () => {
    return (
      <div className="lg:maxw-xss lg:w-auto logo-holder">
        {data.hasOwnProperty("icon") ? (
          <>
            <figure
              className="desktop-only bg-white br-8 light-shadow md:d-none"
              dangerouslySetInnerHTML={{ __html: data.icon }}
            />
            {data.hasOwnProperty("iconMobile") ? (
              <figure
                className="lg:d-none"
                dangerouslySetInnerHTML={{ __html: data.iconMobile }}
              />
            ) : (
              <figure
                className="lg:d-none"
                dangerouslySetInnerHTML={{ __html: data.icon }}
              />
            )}
          </>
        ) : (
          <>
            <figure className="bg-white br-8 lg:pb-12.5 lg:pt-12.5 lg:px-4 light-shadow md:d-none">
              <ResponsiveImage
                src={data.img.src}
                alt={data.img.alt}
                mobile={data.img.prefixes.mobile}
                dpi={data.img.prefixes.dpi}
              />
            </figure>
            <figure className="lg:d-none">
              <ResponsiveImage
                src={data.img.src}
                alt={data.img.alt}
                mobile={data.img.prefixes.mobile}
                dpi={data.img.prefixes.dpi}
                imgClass="md:w-75"
              />
            </figure>
          </>
        )}
        {data.hasOwnProperty("iconZap") && (
          <div className="zap-icon lg:mb-1 lg:mt-3 md:mt-2">
            <span className="mr-2 text-light-grayish-blue text-xs">VIA</span>
            <figure
              className="bg-white"
              dangerouslySetInnerHTML={{ __html: data.iconZap }}
            />
          </div>
        )}
        <style jsx>{`
          @media (min-width: ${viewport.tablet.sm}) {
            .logo-holder {
              position: sticky;
              top: 180px;
            }
          }
          .desktop-only {
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 260px;
            max-width: 260px;
            text-align: center;
            min-height: 260px;
            max-height: 260px;
          }
          .zap-icon {
            display: flex;
            align-items: center;
            justify-content: end;
          }
          .zap-icon :global(figure) {
            width: 96px;
            height: 48px;
            padding: 5.6px 12.2px 9.6px 12.2px;
            border-radius: 4px;
          }
          @media (max-width: ${viewport.tablet.sm}) {
            .zap-icon {
              justify-content: start;
            }
          }
        `}</style>
      </div>
    );
  };
  const Content = () => {
    return (
      <div className="logo-content-holder lg:maxw-xl w-100 content-sticky">
        {data.content.map((item, index) => {
          return (
            <article
              key={index}
              className={`text-left ${index === 0 ? `` : `lg:mt-10`}`}
            >
              {item.map((item, index) => {
                // return <p>{item.heading}</p>;
                if (item.hasOwnProperty("heading")) {
                  return <Heading data={item.heading} key={index} />;
                }
                if (item.hasOwnProperty("text")) {
                  return <Para data={item.text} key={index} />;
                }
                if (item.hasOwnProperty("ol")) {
                  return <OrderedList data={item.ol} key={index} />;
                }
                if (item.hasOwnProperty("ul")) {
                  return <UnOrderedList data={item.ul} key={index} />;
                }
                if (item.hasOwnProperty("video")) {
                  return <Video data={item.video} key={index} />;
                }
                // For Images
                if (item.hasOwnProperty("image")) {
                  return (
                    <ResponsiveImage
                      key={index}
                      loading="lazy"
                      alt={item.image.alt}
                      src={item.image.src}
                      width={item.image.dimensions[0]}
                      height={item.image.dimensions[1]}
                      dpi={item.image.prefixes.dpi}
                      mobile={item.image.prefixes.mobile}
                      hasMobileImg={
                        item.image.prefixes.hasOwnProperty("mobile")
                          ? item.image.prefixes.mobile
                          : false
                      }
                      wrapClass="lg:mt-3 lg:mb-3 md:mt-2.5 md:mb-2"
                    />
                  );
                }
                // For gif
                if (item.hasOwnProperty("gif")) {
                  return (
                    <img
                      key={index}
                      width="760"
                      height="420"
                      className="img-fit md:w-100 br-8 lg:mt-3 md:mt-2.5"
                      src={item.gif.src}
                      alt={item.gif.alt}
                    />
                  );
                }
                // For Note Text
                if (item.hasOwnProperty("note")) {
                  return <NoteText key={index}>{item.note}</NoteText>;
                }
              })}

              {/*
              {item.hasOwnProperty("heading") && (
                <Heading data={item.heading} />
              )}
              {item.hasOwnProperty("link") && <Link data={item.link} />}
              {item.hasOwnProperty("desc") && <Para data={item.desc} />}
              {item.hasOwnProperty("ol") && <OrderedList data={item.ol} />}
              {item.hasOwnProperty("ul") && <UnOrderedList data={item.ul} />} */}
            </article>
          );
        })}
        <style jsx>{`
          @media (min-width: ${viewport.tablet.sm}) {
            .logo-holder {
              position: sticky;
              top: 100px;
            }
            .content-sticky {
              left: 140px !important;
            }
          }
          // sticky content column was breaking from 1024px
          @media (max-width: 1024px) and (min-width: ${viewport.tablet.sm}) {
            .content-sticky {
              width: 50% !important;
              left: 86px !important;
            }
          }
        `}</style>
      </div>
    );
  };
  return (
    <section className={`bg-light lg:mt-20 ${className}`}>
      <div className={`container grid--default has-align-top`}>
        <Logo />
        <Content />
      </div>
    </section>
  );
}
